<template>
  <component
    :is="iconComponent"
    :color="computedColor"
  />
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import ArrowRightIcon from './icons/svg/ArrowRightIcon.vue';
import ArrowLeftIcon from './icons/svg/ArrowLeftIcon.vue';
import StandardButtonLoader from './icons/svg/StandardButtonLoader.vue';
import ShowAllInstructionIcon from '../AsACoachCheckpointInspectionBar/showAllInstructionIcon.vue';
import PlusLeftIcon from './icons/svg/PlusLeftIcon.vue';
import DetachIcon from './icons/svg/DetachIcon.vue';
import ChevronLeftBack from '../CheckpointInspectionNavigator/ChevronLeftBack.vue';
import ChevronLeftDoubleBack from '../CheckpointInspectionNavigator/ChevronLeftDoubleBack.vue';
import ChevronRightForward from '../CheckpointInspectionNavigator/ChevronRightForward.vue';
import CornerUpLeftIcon from './icons/svg/CornerUpLeftIcon.vue';

/**
 * Icon registry mapping icon names to their Vue components
 * @type {Object}
 */
const iconRegistry = {
  arrowRight: ArrowRightIcon,
  arrowLeft: ArrowLeftIcon,
  loader: StandardButtonLoader,
  showAllInstructionIcon: ShowAllInstructionIcon,
  plusLeft: PlusLeftIcon,
  detachIcon: DetachIcon,
  chevronLeftDoubleBack: ChevronLeftDoubleBack,
  chevronLeftBack: ChevronLeftBack,
  chevronRightForward: ChevronRightForward,
  cornerUpLeft: CornerUpLeftIcon
};

/**
 * Get the appropriate icon color based on button type, variant, and disabled state
 * @param {String} buttonType - The button type (magenta, red, green, terminated)
 * @param {String} variant - The button variant (primary, secondary, etc.)
 * @param {Boolean} disabled - Whether the button is disabled
 * @returns {String} The hex color code for the icon
 */
const getIconColor = (buttonType, variant, disabled = false) => {
  if (disabled) {
    // For disabled buttons, use muted colors
    const disabledColors = {
      magenta: {
        primary: '#FFFFFF',
        secondary: '#E0E0E0',
        tertiary: '#E0E0E0',
        outline: '#E0E0E0',
        'text-secondary': '#E0E0E0',
        'text-primary': '#E0E0E0'
      },
      red: {
        primary: '#FFFFFF',
        secondary: '#E0E0E0',
        tertiary: '#E0E0E0',
        outline: '#E0E0E0',
        'text-secondary': '#E0E0E0',
        'text-primary': '#d1d5db'
      },
      green: {
        primary: '#FFFFFF',
        secondary: '#E0E0E0',
        tertiary: '#E0E0E0',
        outline: '#E0E0E0',
        'text-secondary': '#E0E0E0',
        'text-primary': '#d1d5db'
      },
      terminated: {
        primary: '#FFFFFF'
      }
    };
    return disabledColors[buttonType]?.[variant] || '#E0E0E0';
  } else {
    // For enabled buttons, use the brand colors
    const colorMap = {
      magenta: {
        primary: '#ffffff',
        secondary: '#7F56D9',
        tertiary: '#7F56D9',
        outline: '#344054',
        'text-primary': '#7F56D9',
        'text-secondary': '#344054'
      },
      red: {
        primary: '#ffffff',
        secondary: '#D92D20',
        tertiary: '#D92D20',
        outline: '#D92D20',
        'text-primary': '#D92D20',
        'text-secondary': '#344054'
      },
      green: {
        primary: '#ffffff',
        secondary: '#17B26A',
        tertiary: '#17B26A',
        outline: '#17B26A',
        'text-primary': '#17B26A',
        'text-secondary': '#344054'
      },
      terminated: {
        primary: '#ffffff'
      }
    };
    return colorMap[buttonType]?.[variant] || '#000000';
  }
};

/**
 * StandardButtonIcons Component Props
 * Handles icon rendering with appropriate colors based on button state
 */
const props = defineProps({
  /**
   * Icon name to display
   * @type {String}
   * @required
   * @values 'arrowRight', 'arrowLeft', 'loader'
   */
  name: {
    type: String,
    required: true,
    validator(value) {
      return [
        'arrowRight',
        'arrowLeft',
        'loader',
        'showAllInstructionIcon',
        'plusLeft',
        'detachIcon',
        'chevronLeftBack',
        'chevronLeftDoubleBack',
        'chevronRightForward',
        'cornerUpLeft'
      ].includes(value);
    }
  },

  /**
   * Button type for color calculation
   * @type {String}
   * @default 'magenta'
   * @values 'magenta', 'red', 'green', 'terminated'
   */
  buttonType: {
    type: String,
    default: 'magenta',
    validator: (value) => ['magenta', 'red', 'green', 'terminated'].includes(value)
  },

  /**
   * Button variant for color calculation
   * @type {String}
   * @default 'primary'
   * @values 'primary', 'secondary', 'tertiary', 'outline', 'text-secondary', 'text-primary'
   */
  buttonVariant: {
    type: String,
    default: 'primary',
    validator: (value) => [
      'primary', 'secondary', 'tertiary', 'outline',
      'text-secondary', 'text-primary'
    ].includes(value)
  },

  /**
   * Whether the button is disabled
   * @type {Boolean}
   * @default false
   */
  disabled: {
    type: Boolean,
    default: false
  },

  /**
   * Custom color override (optional)
   * @type {String}
   * @default null
   */
  color: {
    type: String,
    default: null
  }
});

// Reactive icon component reference
const iconComponent = ref(null);

/**
 * Computed color for the icon
 * Uses custom color if provided, otherwise calculates based on button state
 * @returns {String} The hex color code for the icon
 */
const computedColor = computed(() => {
  if (props.color) {
    return props.color;
  }
  return getIconColor(props.buttonType, props.buttonVariant, props.disabled);
});

/**
 * Set the icon component based on the name prop
 * Updates the reactive iconComponent reference
 */
const setIconComponent = () => {
  if (iconRegistry[props.name]) {
    iconComponent.value = iconRegistry[props.name];
  }
};

// Initialize icon component on mount
onMounted(() => {
  setIconComponent();
});

// Watch for changes in icon name and update component
watch(() => props.name, () => {
  setIconComponent();
});
</script>

<style scoped></style>
